import { MatterSignatureRequestContextMenu } from "@/components/features/matter/context-menus/MatterSignatureRequestContextMenu";
import { Badge, Button, Card, CardContent, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  Matter,
  MatterContactConnection,
  MatterSignatureRequest,
  MatterUserConnection,
} from "@/lib/types";
import type { ReportsSignatureRequestReport } from "@/lib/types/Reports";
import { cn } from "@/lib/utils/cn";
import { formatDate, formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import { ContactEmailIcon, ContactPhoneIcon } from "@/lib/utils/iconUtils";
import dayjs from "dayjs";
import { Pencil } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import {
  buildSignatureRequestRecipientRows,
  type SignatureRequestRecipientRow,
} from "./signatureRequestRecipientsTable";

export type MatterSignatureRequestListItem = MatterSignatureRequest & {
  matterSignatureRequestContactSigners: MatterContactConnection[];
  matterSignatureRequestUserSigners: MatterUserConnection[];
};

const getSignatureRequestStatus = (
  contactSigners: MatterContactConnection[],
  userSigners: MatterUserConnection[],
  expirationDate?: string,
) => {
  if (contactSigners.length === 0 && userSigners.length === 0) {
    return {
      label: "No signers",
      variant: "outline" as const,
    };
  }

  const hasExpired = expirationDate && dayjs().isAfter(dayjs(expirationDate));
  const allSigned =
    contactSigners.every((signer) => !!signer.signatureRequestComplete) &&
    userSigners.every((signer) => !!signer.signatureRequestComplete);

  if (allSigned) {
    return {
      label: "Completed",
      variant: "green" as const,
    };
  }

  if (hasExpired) {
    return {
      label: "Expired",
      variant: "destructive" as const,
    };
  }

  return {
    label: "Pending",
    variant: "warning" as const,
  };
};

const SignerCommunicationDetails = ({
  row,
}: {
  row: SignatureRequestRecipientRow;
}) => {
  return (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
      <Text
        variant="subHeading"
        as="span"
        className="flex items-center gap-x-1"
      >
        <ContactEmailIcon className="h-3.5 w-3.5" />
        {row.email || "No email address"}
      </Text>
      <Text
        variant="subHeading"
        as="span"
        className="flex items-center gap-x-1"
      >
        <ContactPhoneIcon className="h-3.5 w-3.5" />
        {row.phoneNumber
          ? formatLocalOrIntlPhoneNumber({
              number: row.phoneNumber,
              extension: row.phoneExtension,
            })
          : "No phone number"}
      </Text>
    </div>
  );
};

type MatterSignatureRequestItemProps = {
  matter: Matter;
  signatureRequest: MatterSignatureRequestListItem;
  contextLabel?: ReactNode;
  /** When set (e.g. global signature requests list firm row), opens the firm update slideover. */
  firmSignatureReportRow?: ReportsSignatureRequestReport;
  signatureRequestPresentation?: "slideover" | "modal";
  showSubItemControls?: boolean;
};

export const MatterSignatureRequestItem = ({
  matter,
  signatureRequest,
  contextLabel,
  firmSignatureReportRow,
  signatureRequestPresentation = "slideover",
  showSubItemControls = true,
}: MatterSignatureRequestItemProps) => {
  const { openFirmSignatureRequestModal, openSignatureRequestModal } =
    useGlobalModal();
  const { hasActionPermission } = useUser();
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSignatureRequest,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSignatureRequest,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canOpenExistingSignatureRequest =
    hasUpdatePermission || hasDeletePermission;
  const hasMatterContext = Boolean(matter.id);
  const showEditButton =
    canOpenExistingSignatureRequest &&
    (hasMatterContext || Boolean(firmSignatureReportRow));

  const status = getSignatureRequestStatus(
    signatureRequest.matterSignatureRequestContactSigners,
    signatureRequest.matterSignatureRequestUserSigners,
    signatureRequest.matterSignatureRequestExpirationDate,
  );
  const totalSigners =
    signatureRequest.matterSignatureRequestContactSigners.length +
    signatureRequest.matterSignatureRequestUserSigners.length;
  const completedSigners =
    signatureRequest.matterSignatureRequestContactSigners.filter(
      (signer) => signer.signatureRequestComplete,
    ).length +
    signatureRequest.matterSignatureRequestUserSigners.filter(
      (signer) => signer.signatureRequestComplete,
    ).length;
  const signers = useMemo(
    () =>
      buildSignatureRequestRecipientRows({
        contactSigners: signatureRequest.matterSignatureRequestContactSigners,
        userSigners: signatureRequest.matterSignatureRequestUserSigners,
      }),
    [
      signatureRequest.matterSignatureRequestContactSigners,
      signatureRequest.matterSignatureRequestUserSigners,
    ],
  );
  const handleEdit = () => {
    if (firmSignatureReportRow) {
      openFirmSignatureRequestModal({ reportRow: firmSignatureReportRow });
      return;
    }
    openSignatureRequestModal({
      matterId: matter.id,
      signatureRequestId: signatureRequest.matterSignatureRequestId,
      initialTab: "details",
      presentation: signatureRequestPresentation,
    });
  };

  return (
    <MatterSignatureRequestContextMenu
      enabled={hasMatterContext && showSubItemControls}
      matter={matter}
      onEdit={handleEdit}
      signatureRequest={signatureRequest}
      trigger={
        <Card
          className="border-border hover:bg-muted/50 group w-full cursor-pointer transition-colors"
          data-testid="signature-request-card"
          data-signature-request-id={signatureRequest.matterSignatureRequestId}
        >
          <div className="flex items-start justify-between p-6 pb-2">
            <div className="min-w-0">
              {contextLabel ? <div className="mb-2">{contextLabel}</div> : null}
              <div className="flex flex-wrap items-center gap-2">
                <Text variant="cardTitle" className="mb-0 break-all">
                  {signatureRequest.matterFileName}
                </Text>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>
              <div className="mt-2 flex flex-col gap-y-1">
                <div className="flex items-center gap-x-1">
                  <Text variant="subHeading" className="self-start">
                    Expiration date:
                  </Text>
                  <Text variant="body" className="font-semibold">
                    {formatDate(
                      signatureRequest.matterSignatureRequestExpirationDate,
                    ) ?? "No Expiration Date"}
                  </Text>
                </div>
                <div className="flex items-center gap-x-1">
                  <Text variant="subHeading" className="self-start">
                    Signing progress:
                  </Text>
                  <Text variant="body" className="font-semibold">
                    {totalSigners === 0
                      ? "No signers assigned"
                      : `${completedSigners}/${totalSigners} signed`}
                  </Text>
                </div>
              </div>
            </div>
            {showEditButton ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                data-testid="edit-signature-request-button"
                onClick={handleEdit}
                className={cn(
                  "text-muted-foreground hover:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 h-8 w-8 shrink-0",
                  "opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100",
                )}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
          <CardContent className="px-6 pt-0 pb-4">
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Text variant="subHeading" className="font-medium">
                  Signers
                </Text>
                {signers.some((s) => s.order != null) && (
                  <Badge variant="outline">Ordered</Badge>
                )}
              </div>
              {signers.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {signers.map((row) => {
                    const signerStatus = row.isSigned
                      ? "Signed"
                      : status.label === "Expired"
                        ? "Expired"
                        : "Pending";
                    const signerStatusVariant =
                      signerStatus === "Signed"
                        ? "green"
                        : signerStatus === "Expired"
                          ? "destructive"
                          : "warning";

                    return (
                      <div
                        key={
                          row.kind === "contact"
                            ? row.contactConnection?.id
                            : row.userConnection?.id
                        }
                        className="flex flex-col gap-1"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          {row.order != null && (
                            <Text
                              variant="subHeading"
                              as="span"
                              className="text-muted-foreground tabular-nums"
                            >
                              {row.order}.
                            </Text>
                          )}
                          <Text variant="body" className="font-semibold">
                            {row.displayName}
                          </Text>
                          <Badge
                            variant={row.kind === "contact" ? "blue" : "purple"}
                          >
                            {row.kind === "contact" ? "Contact" : "Firm"}
                          </Badge>
                          <Badge variant={signerStatusVariant}>
                            {signerStatus}
                          </Badge>
                        </div>
                        <SignerCommunicationDetails row={row} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Text variant="subHeading">-</Text>
              )}
            </div>
          </CardContent>
        </Card>
      }
    />
  );
};
